import { resolveTenantTimezone } from "@/lib/scheduling/tenant-timezone"
import type { SchedulingSlot } from "@/lib/scheduling/types"
import type { DayOfWeek, ServiceType } from "@/types"

const DAY_NAMES_PT: Record<DayOfWeek, string> = {
  SUNDAY: "domingo",
  MONDAY: "segunda-feira",
  TUESDAY: "terça-feira",
  WEDNESDAY: "quarta-feira",
  THURSDAY: "quinta-feira",
  FRIDAY: "sexta-feira",
  SATURDAY: "sábado",
}

const WEEKDAY_CODES: Record<string, DayOfWeek> = {
  Sun: "SUNDAY",
  Mon: "MONDAY",
  Tue: "TUESDAY",
  Wed: "WEDNESDAY",
  Thu: "THURSDAY",
  Fri: "FRIDAY",
  Sat: "SATURDAY",
}

type ZonedInstant = {
  /** "YYYY-MM-DD" of the instant on the store's calendar. */
  day: string
  /** "HH:mm" of the instant on the store's wall clock. */
  time: string
  weekday: DayOfWeek
}

function zonedInstant(date: Date, timeZone?: string): ZonedInstant | null {
  if (Number.isNaN(date.getTime())) return null

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone ? resolveTenantTimezone(timeZone) : undefined,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date)

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ""

  const hour = value("hour") === "24" ? "00" : value("hour")

  return {
    day: `${value("year")}-${value("month")}-${value("day")}`,
    time: `${hour}:${value("minute")}`,
    weekday: WEEKDAY_CODES[value("weekday")] ?? "SUNDAY",
  }
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000)
}

/**
 * "HH:mm" of an RFC3339 instant on the store's wall clock. `timeZone` is the
 * tenant zone from the scheduling context (§2A.4); omitting it falls back to
 * the customer's own clock, which only agrees when both share a zone.
 */
export function formatSlotTime(isoInstant: string, timeZone?: string): string {
  return zonedInstant(new Date(isoInstant), timeZone)?.time ?? ""
}

/** Today / tomorrow / weekday name for an instant on the store calendar. */
export function formatDayLabel(
  isoInstant: string,
  now: Date = new Date(),
  timeZone?: string,
): string {
  const instant = zonedInstant(new Date(isoInstant), timeZone)
  const reference = zonedInstant(now, timeZone)
  if (!instant || !reference) return ""

  const days = daysBetween(reference.day, instant.day)
  if (days === 0) return "hoje"
  if (days === 1) return "amanhã"
  return DAY_NAMES_PT[instant.weekday]
}

/** Day and opening time of the target window. */
export function formatWindowOpening(
  isoInstant: string,
  now: Date = new Date(),
  timeZone?: string,
): string {
  const label = formatDayLabel(isoInstant, now, timeZone)
  if (!label) return ""
  return `${label} às ${formatSlotTime(isoInstant, timeZone)}`
}

/**
 * What a slot means to the customer on each channel: DELIVERY shows the
 * departure plus an ETA-based arrival estimate, TAKEOUT the pickup time.
 */
export function formatSlotChoice(
  slot: SchedulingSlot,
  serviceType: ServiceType,
  deliveryEtaMinutes?: number,
  timeZone?: string,
): string {
  const { title, subtitle } = formatSlotChoiceParts(slot, serviceType, deliveryEtaMinutes, timeZone)
  return subtitle ? `${title} · ${subtitle}` : title
}

/** `formatSlotChoice` split for a slot card: departure (or pickup) as the title, the arrival estimate as the subtitle. */
export function formatSlotChoiceParts(
  slot: SchedulingSlot,
  serviceType: ServiceType,
  deliveryEtaMinutes?: number,
  timeZone?: string,
): { title: string; subtitle?: string } {
  const readyAt = formatSlotTime(slot.readyAt, timeZone)
  if (serviceType !== "DELIVERY") return { title: `Retirar às ${readyAt}` }
  if (!deliveryEtaMinutes || deliveryEtaMinutes <= 0) return { title: `Sai da loja às ${readyAt}` }

  const arrival = new Date(new Date(slot.readyAt).getTime() + deliveryEtaMinutes * 60_000)
  return {
    title: `Sai da loja às ${readyAt}`,
    subtitle: `chega por volta de ${formatSlotTime(arrival.toISOString(), timeZone)}`,
  }
}

/** Scheduled-for line — order tracking and review copy. */
export function formatScheduledFor(
  isoInstant: string,
  now: Date = new Date(),
  timeZone?: string,
): string {
  return `Agendado para ${formatWindowOpening(isoInstant, now, timeZone)}`
}

const SHORT_DAY_NAMES_PT: Record<DayOfWeek, string> = {
  SUNDAY: "dom",
  MONDAY: "seg",
  TUESDAY: "ter",
  WEDNESDAY: "qua",
  THURSDAY: "qui",
  FRIDAY: "sex",
  SATURDAY: "sáb",
}

function dayDate(isoDate: string): string {
  const [, month = "", day = ""] = isoDate.split("-")
  return day && month ? `${day}/${month}` : ""
}

function weekdayOfDate(isoDate: string, timeZone?: string): DayOfWeek | null {
  const instant = zonedInstant(new Date(`${isoDate}T12:00:00Z`), timeZone)
  return instant ? instant.weekday : null
}

/**
 * Label of a day chip in the full model: today/tomorrow stay relative;
 * further out carries the date the customer will recognise.
 */
export function formatDayChip(isoDate: string, now: Date = new Date(), timeZone?: string): string {
  const reference = zonedInstant(now, timeZone)
  if (!reference) return dayDate(isoDate)

  const days = daysBetween(reference.day, isoDate)
  if (days === 0) return "hoje"
  if (days === 1) return "amanhã"

  const weekday = weekdayOfDate(isoDate, timeZone)
  return weekday ? `${SHORT_DAY_NAMES_PT[weekday]} ${dayDate(isoDate)}` : dayDate(isoDate)
}

/**
 * One entry of the arrival list: DELIVERY commits to the WINDOW the customer
 * was promised, TAKEOUT to the hour the counter has it ready (D3).
 */
export function formatArrivalChoice(
  slot: { arrivalStart: string; arrivalEnd: string },
  serviceType: ServiceType,
  timeZone?: string,
): string {
  const start = formatSlotTime(slot.arrivalStart, timeZone)
  if (serviceType !== "DELIVERY") return start
  const end = formatSlotTime(slot.arrivalEnd, timeZone)
  return end && end !== start ? `${start} – ${end}` : start
}

/**
 * Review line for DELIVERY or TAKEOUT — always the window, never the instant.
 */
export function formatArrivalCommitment(
  slot: { arrivalStart: string; arrivalEnd: string },
  serviceType: ServiceType,
  timeZone?: string,
): string {
  const instant = zonedInstant(new Date(slot.arrivalStart), timeZone)
  if (!instant) return ""

  const day = `${DAY_NAMES_PT[instant.weekday].replace("-feira", "")} ${dayDate(instant.day)}`
  const start = formatSlotTime(slot.arrivalStart, timeZone)

  if (serviceType !== "DELIVERY") return `Retirar ${day} a partir das ${start}`

  const end = formatSlotTime(slot.arrivalEnd, timeZone)
  if (!end || end === start) return `Chega ${day} às ${start}`
  return `Chega ${day} entre ${start} e ${end}`
}
